// @ts-nocheck
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, Zap, X, Clock, BookOpen, Sparkles } from 'lucide-react';
import { apiJSON } from '../config/api';
import './Notifications.css';

const Notifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({ unread_notifications: 0 });

  const fetchNotifications = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await apiJSON('notifications?unread_only=true&limit=20');
      setNotifications(data.notifications || []);
    } catch (error) {
      console.error("Erreur de chargement des notifications:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const data = await apiJSON('notifications/stats');
      setStats(data.statistics || { unread_notifications: 0 });
    } catch (error) {
      console.error("Erreur de chargement des statistiques:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
    fetchStats();
    
    // Polling toutes les 2 minutes
    const interval = setInterval(() => {
      fetchNotifications();
      fetchStats();
    }, 120000);
    
    return () => clearInterval(interval);
  }, [fetchNotifications, fetchStats]);

  const markAsRead = async (id) => {
    try {
      await apiJSON(`notifications/${id}/read`, { method: 'POST' });
      setNotifications(prev => prev.filter(n => n.id !== id));
      setStats(prev => ({
        ...prev,
        unread: Math.max(0, (prev.unread || 0) - 1)
      }));
    } catch (error) {
      console.error("Erreur lors de la mise à jour de la notification:", error);
    }
  };

  const markAllAsRead = async () => {
    try {
      for (const notification of notifications) {
        await apiJSON(`notifications/${notification.id}/read`, { method: 'POST' });
      }
      setNotifications([]);
      setStats(prev => ({ ...prev, unread: 0 }));
    } catch (error) {
      console.error("Erreur lors de la mise à jour des notifications:", error);
    }
  };

  const runAgent = async () => {
    try {
      setIsLoading(true);
      await apiJSON('notifications/run-agent', { method: 'POST' });
      // Recharger les notifications après l'exécution de l'agent
      setTimeout(() => {
        fetchNotifications();
        fetchStats();
      }, 2000);
    } catch (error) {
      console.error("Erreur lors de l'exécution de l'agent:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return date.toLocaleDateString('fr-FR');
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'serendipity':
        return <Sparkles size={16} className="notification-icon serendipity" />;
      case 'system_alert':
        return <Bell size={16} className="notification-icon system" />;
      default:
        return <Zap size={16} className="notification-icon default" />;
    }
  };

  const unreadCount = stats.unread_notifications || notifications.length;

  return (
    <div className="notification-center">
      <button 
        onClick={() => setIsOpen(!isOpen)} 
        className="notification-bell"
        title="Notifications"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="notification-dropdown">
          <div className="dropdown-header">
            <div className="header-content">
              <h3>
                <Sparkles size={20} />
                Notifications
              </h3>
              <span className="notification-count">
                {unreadCount} non lue{unreadCount > 1 ? 's' : ''}
              </span>
            </div>
            <div className="header-actions">
              {notifications.length > 0 && (
                <button 
                  onClick={markAllAsRead}
                  className="mark-all-read"
                  title="Marquer tout comme lu"
                >
                  Tout marquer comme lu
                </button>
              )}
              <button 
                onClick={runAgent}
                disabled={isLoading}
                className="run-agent-btn"
                title="Lancer l'agent de sérendipité"
              >
                {isLoading ? '...' : <Zap size={16} />}
              </button>
              <button 
                onClick={() => setIsOpen(false)}
                className="close-btn"
                title="Fermer"
              >
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="dropdown-body">
            {isLoading ? (
              <div className="loading-notifications">
                <div className="loading-spinner"></div>
                <p>Chargement des notifications...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="no-notifications">
                <Sparkles size={48} className="empty-icon" />
                <h4>Aucune nouvelle suggestion</h4>
                <p>L'agent de sérendipité analysera automatiquement vos nouveaux livres et vous proposera des connexions intéressantes.</p>
                <button onClick={runAgent} className="run-agent-empty">
                  <Zap size={16} />
                  Lancer l'agent maintenant
                </button>
              </div>
            ) : (
              <div className="notifications-list">
                {notifications.map(notif => (
                  <div key={notif.id} className="notification-item">
                    <div className="notif-icon">
                      {getNotificationIcon(notif.type)}
                    </div>
                    <div className="notif-content">
                      <div className="notif-header">
                        <h4>{notif.title}</h4>
                        <span className="notif-time">
                          <Clock size={16} />
                          {formatTimestamp(notif.created_at)}
                        </span>
                      </div>
                      <p>{notif.message}</p>
                      {notif.related_book_id && (
                        <div className="related-book">
                          <BookOpen size={16} />
                          <span>Livre associé: {notif.related_book_id}</span>
                        </div>
                      )}
                    </div>
                    <button 
                      onClick={() => markAsRead(notif.id)} 
                      className="notif-dismiss"
                      title="Marquer comme lu"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {notifications.length > 0 && (
            <div className="dropdown-footer">
              <button onClick={() => setIsOpen(false)} className="view-all-btn">
                Voir toutes les notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Notifications;
